import { useState } from "react";
import { ChevronRight, ExternalLink, Lock, LockOpen, Plus, Route, UserRound } from "lucide-react";

import { Input } from "@monorepo-apps/ui/src/components/input";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { backlogTargetIteration } from "@/application/use-cases/b2c/backlog-triage";
import type { IterationView, WorkstreamView } from "@/application/use-cases/b2c/build-b2c-board";
import { ActionButton } from "@/presentation/components/delivery-tasks/action-button";
import type { BacklogActions } from "@/presentation/components/b2c/backlog-actions";
import { BacklogPanel } from "@/presentation/components/b2c/backlog-panel";
import {
  IterationBlock,
  type IterationActions,
} from "@/presentation/components/b2c/iteration-block";
import { CopyCode } from "@/presentation/components/b2c/copy-code";
import { EditableDateRange } from "@/presentation/components/b2c/editable-date-range";
import { EditableTitle } from "@/presentation/components/b2c/editable-title";
import type { WorkstreamPatch } from "@/infrastructure/repositories/b2c-board.supabase.repository";
import { MarathonProgress } from "@/presentation/components/b2c/marathon-progress";
import { StatusDot } from "@/presentation/components/status/status-dot";
import { ITERATION_STATUS_LABEL, ITERATION_STATUS_TONE } from "@/presentation/data/b2c-board";

/**
 * One workstream: who runs it, which third parties it touches, which stage-1
 * workstreams it builds on (its gate), its end-to-end proof, and its iterations.
 */
export function WorkstreamCard({
  view,
  canWrite,
  open,
  onToggle,
  onPatch,
  onNewIteration,
  actionsFor,
  startBlockedReasonFor,
  backlogActions,
}: {
  view: WorkstreamView;
  canWrite: boolean;
  open: boolean;
  onToggle: () => void;
  onPatch: (patch: WorkstreamPatch) => void;
  /** Absent while an iteration is still waiting to start; otherwise opens the next, stacked if needed. */
  onNewIteration: (() => void) | null;
  actionsFor: (iteration: IterationView) => IterationActions;
  startBlockedReasonFor: (iteration: IterationView) => string | null;
  backlogActions: BacklogActions;
}) {
  const { workstream, current, buildsOn, gateOpen } = view;
  const [editingOwner, setEditingOwner] = useState(false);
  const [owner, setOwner] = useState(workstream.owner_name ?? "");
  const merged = view.iterations.filter((i) => i.iteration.status === "merged");
  const openIterations = view.iterations.filter((i) => i.iteration.status !== "merged");
  const lastUnmerged = openIterations[openIterations.length - 1] ?? null;
  // The output document is one living page per workstream, rewritten each iteration: the newest
  // iteration that recorded a link carries the workstream's current one.
  const outputUrl =
    [...view.iterations]
      .sort((a, b) => b.iteration.number - a.iteration.number)
      .find((i) => i.iteration.output_url)?.iteration.output_url ?? null;

  return (
    <article
      className={cn(
        "glow-card-static flex flex-col gap-3 p-4",
        current?.iteration.status === "marathon" &&
          "outline-2 -outline-offset-2 outline-[oklch(0.74_0.16_80_/_0.45)]"
      )}
    >
      <header className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? `Collapse ${workstream.title}` : `Expand ${workstream.title}`}
          className="text-muted-foreground hover:text-foreground mt-0.5 shrink-0"
        >
          <ChevronRight className={cn("size-4 transition-transform", open && "rotate-90")} />
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <CopyCode code={workstream.code} className="text-primary text-[13px] font-bold" />
            <EditableTitle
              value={workstream.title}
              canWrite={canWrite}
              onChange={(title) => onPatch({ title })}
              ariaLabel={`Title of workstream ${workstream.code}`}
              className="px-0.5 text-[15px] leading-tight font-semibold"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {workstream.third_parties.map((p) => (
              <span
                key={p}
                className="border-border font-data text-muted-foreground rounded-md border px-1.5 py-0.5 text-[10px]"
              >
                {p}
              </span>
            ))}
            {editingOwner ? (
              <form
                className="flex items-center gap-1.5"
                // Enter blurs the field; the save happens once, on blur.
                onSubmit={(e) => {
                  e.preventDefault();
                  (e.currentTarget.elements.namedItem("owner") as HTMLInputElement | null)?.blur();
                }}
              >
                <Input
                  name="owner"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  autoFocus
                  placeholder="Owner"
                  className="h-6 w-36 text-[11px]"
                  aria-label={`Owner of ${workstream.title}`}
                  onBlur={() => {
                    const next = owner.trim() || null;
                    if (next !== workstream.owner_name) onPatch({ owner_name: next });
                    setEditingOwner(false);
                  }}
                />
              </form>
            ) : (
              <button
                type="button"
                disabled={!canWrite}
                onClick={() => {
                  // Re-seed from the current row: it may have changed since mount.
                  setOwner(workstream.owner_name ?? "");
                  setEditingOwner(true);
                }}
                className={cn(
                  "flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px]",
                  workstream.owner_name ? "text-foreground" : "text-muted-foreground italic",
                  canWrite && "hover:bg-muted"
                )}
              >
                <UserRound className="size-3" aria-hidden />
                {workstream.owner_name ?? "No owner yet"}
              </button>
            )}
            <EditableDateRange
              start={workstream.start_date}
              end={workstream.end_date}
              canWrite={canWrite}
              onChange={onPatch}
              label={workstream.title}
            />
            {outputUrl ? (
              <a
                href={outputUrl}
                target="_blank"
                rel="noreferrer"
                className="text-primary flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold hover:underline"
              >
                <ExternalLink className="size-3" aria-hidden />
                Marathon output
              </a>
            ) : null}
          </div>
        </div>
        {current ? (
          <span className="flex shrink-0 items-center gap-1.5 text-[11px]">
            <StatusDot
              tone={ITERATION_STATUS_TONE[current.iteration.status]}
              pulse={current.iteration.status === "marathon"}
            />
            <span className="font-data">It. {current.iteration.number}</span>
          </span>
        ) : null}
      </header>

      {buildsOn.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          {gateOpen ? (
            <LockOpen
              className="size-3.5 text-[var(--rag-green)]"
              role="img"
              aria-label="Gate open"
            />
          ) : (
            <Lock className="text-muted-foreground size-3.5" role="img" aria-label="Gate closed" />
          )}
          <span className="text-muted-foreground">Builds on</span>
          {buildsOn.map((g) => (
            <span
              key={g.key}
              title={`${g.title} — ${g.merged ? "merged" : "not merged yet"}`}
              className={cn(
                "font-data rounded-md px-1.5 py-0.5",
                g.merged
                  ? "bg-[oklch(0.66_0.16_155_/_0.12)] text-[oklch(0.45_0.13_155)]"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {g.code}
            </span>
          ))}
        </div>
      ) : null}

      {current ? (
        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground text-[11px]">
            {ITERATION_STATUS_LABEL[current.iteration.status]} · iteration{" "}
            {current.iteration.number}
          </span>
          <MarathonProgress progress={current.progress} status={current.iteration.status} />
        </div>
      ) : null}

      {open ? (
        <div className="flex flex-col gap-3 pt-1">
          {workstream.summary ? (
            <p className="text-muted-foreground text-[12.5px] leading-relaxed">
              {workstream.summary}
            </p>
          ) : null}
          {workstream.e2e_proof ? (
            <div className="flex gap-2 rounded-lg bg-[oklch(0.72_0.17_190_/_0.07)] p-3">
              <Route className="text-primary mt-0.5 size-4 shrink-0" aria-hidden />
              <div className="flex flex-col gap-0.5">
                <span className="font-data text-primary text-[10px] font-bold tracking-[0.16em] uppercase">
                  End-to-end proof
                </span>
                <p className="text-[12.5px] leading-relaxed">{workstream.e2e_proof}</p>
              </div>
            </div>
          ) : null}

          {openIterations.map((iv) => (
            <IterationBlock
              key={iv.iteration.id}
              view={iv}
              canWrite={canWrite}
              startBlockedReason={startBlockedReasonFor(iv)}
              actions={actionsFor(iv)}
            />
          ))}

          {/* Merged iterations stay listed after the open ones, collapsed by default. */}
          {merged.map((iv) => (
            <IterationBlock
              key={iv.iteration.id}
              view={iv}
              canWrite={canWrite}
              startBlockedReason={null}
              actions={actionsFor(iv)}
            />
          ))}

          <BacklogPanel
            tasks={view.unplanned}
            target={backlogTargetIteration(view.iterations)}
            canWrite={canWrite}
            actions={backlogActions}
          />

          {canWrite && onNewIteration ? (
            <div className="flex flex-wrap items-center gap-2">
              <ActionButton
                label="New iteration"
                icon={Plus}
                surface="card"
                onClick={onNewIteration}
              />
              {lastUnmerged ? (
                <span className="text-muted-foreground text-[11px]">
                  It will stack on iteration {lastUnmerged.iteration.number}, which hasn’t merged
                  yet.
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
